import { useCallback, useEffect, useRef, useState } from 'react';

import type { Span } from '../types';

/**
 * ONE preview per bar, owned by whichever gesture is currently running.
 *
 * WHY THIS EXISTS (the C2 composition defect). `useBarDrag` and `useBarKeyboard` each used to hold
 * their own `previewSpan`, merged at the call site with
 * `drag.previewSpan ?? keyboard.previewSpan ?? state.previewSpan`. Nothing cleared one when the
 * other started, and `useBarDrag` measured its delta from `row.span` — the item's REAL dates —
 * rather than from what the user could see. The three consequences, all reproduced:
 *
 * 1. A pointer drag started on top of a pending keyboard preview measured from the wrong origin,
 *    so a `+2` keyboard preview followed by a `+3` drag committed `+3`, not the `+5` the user
 *    aimed at from the bar in front of them.
 * 2. The instant the drag's own preview cleared, the never-cleared keyboard preview reappeared —
 *    the bar snapped back to dates matching neither the item nor the gesture.
 * 3. Pressing `Enter` (the key the bar's own `aria-describedby` hint promises "saves") then diffed
 *    that stale preview against a `span` prop the optimistic write had already moved, and
 *    `patchesFor` faithfully turned the mismatch into a patch nobody chose. It persisted.
 *
 * A single store makes (2) unrepresentable — there is no second preview left to resurface — and
 * gives the two hooks one place to hand ownership over. `baseSpan` closes (3): it records the item
 * span the open preview was computed AGAINST, so a commit can refuse a preview whose base has since
 * moved instead of silently diffing across the change.
 *
 * OPTIONAL, not required, at both hooks' call sites: `GroupBar` (drag only) and any custom
 * `renderers.bar` that calls one hook in isolation get an internal store and behave exactly as
 * before. Sharing is what `Bar` does, because `Bar` is the one component that runs both.
 */
export type SpanPreview = {
  /**
   * The span the bar is currently DRAWING in place of the item's own dates, or `undefined` when no
   * gesture is pending. Every consumer that positions or names a bar must prefer this.
   */
  span?: Span;
  /**
   * The item span `span` was computed from. Not decoration: a preview is only meaningful relative
   * to the dates it was derived from, and both `span` (the prop) and the preview move
   * independently — the prop moves when an optimistic write or a teammate's edit lands. Comparing
   * the two is how a commit tells "the user's pending edit" from "an edit computed against dates
   * that no longer exist".
   */
  baseSpan?: Span;
  /** Open or replace the preview, recording the item span it was computed against. */
  open: (baseSpan: Span, span: Span) => void;
  /** Discard it. Safe to call after unmount — see `mountedRef` below. */
  clear: () => void;
  /**
   * A monotonic id for the LATEST write to this store (`open` or `clear`), read synchronously off
   * a ref rather than from React state.
   *
   * N3 — WHY A SETTLE CALLBACK NEEDS ONE. Every commit path here is asynchronous: `commitSpan`
   * runs `onSettle` when `onCommit`'s promise resolves, which can be long after the gesture that
   * created it ended. The callback then tears the preview down — and until this existed it did so
   * unconditionally, so a LATER gesture's preview was wiped by an EARLIER gesture's settle. The
   * reproduction: press, release (commit in flight), press again before it resolves. Gesture 1's
   * settle cleared gesture 2's preview mid-drag, `useBarDrag`'s `dragMode` went back to `null`
   * with the button still physically down (so `useBarKeyboard`'s `suspended` went false and the
   * arrow keys started writing into the live gesture's own preview), and the eventual release
   * committed a span the bar was no longer drawing. That is the C2 defect class one layer in.
   *
   * A counter inside `useBarDrag` would close the drag-versus-drag half only. The same hole is
   * open on the keyboard path — `Enter`'s settle also clears unconditionally, and a pointerdown
   * landing on the pending preview while that commit is in flight adopts it and then has it
   * pulled out from under the live gesture. Putting the guard on the STORE covers every settle
   * callback, present and future, with one rule, and it is the store — not either hook — that
   * owns the thing being torn down.
   *
   * N8 — AND IT ANSWERS FOR MORE THAN THE STORE. `useBarDrag` releases `dragMode` (and with it
   * `useBarKeyboard`'s `suspended`) on this same predicate rather than on its own "is anything
   * currently held". That was the last piece of shared interaction state still asking a GLOBAL
   * question where an IDENTITY one was needed, and two ordinary sequential drags — both released
   * before either commit landed — separated the two answers into a spurious third commit. The
   * rule to apply to any shared resource a settle callback wants to release: take a version when
   * you take the resource, and only give it back while `isCurrent(version)` still holds. Because
   * every pointer gesture opens the store on `pointerdown`, "still current" and "still the
   * gesture that owns this bar" are the same statement.
   */
  version: () => number;
  /**
   * `true` when nothing has written to this store since `version` was taken, i.e. whoever took it
   * still owns what the bar is drawing. The predicate a settle callback must pass before it
   * discards anything.
   */
  isCurrent: (version: number) => boolean;
};

/** Value equality, because `Span`s are rebuilt from `row.span` on every render. */
export const sameSpan = (a: Span | undefined, b: Span | undefined): boolean =>
  !!a && !!b && a.startDay === b.startDay && a.endDay === b.endDay;

/**
 * `true` when `preview` was computed against something other than the item's CURRENT span — i.e.
 * the dates moved under an open preview (an optimistic write landed, a teammate edited the step).
 * A preview with no base has never been opened and is not stale.
 */
export const isPreviewStale = (preview: SpanPreview, span: Span | undefined): boolean =>
  preview.span !== undefined && !sameSpan(preview.baseSpan, span);

export const useSpanPreview = (): SpanPreview => {
  const [preview, setPreview] = useState<{ span: Span; baseSpan: Span } | null>(null);

  // `onCommit` may be a promise, and the row this bar belongs to can unmount before it settles (a
  // group collapsing, a realtime delete, a route change) — so `clear` has to survive being called
  // into a dead instance.
  //
  // The `true` re-assignment on SETUP, not just the `false` on teardown, is load-bearing under
  // StrictMode and `<Activity>`/Offscreen, both of which unmount effects while KEEPING state and
  // refs. Initialising the ref to `true` at `useRef(true)` and only ever writing `false` meant the
  // second mount inherited `false` forever, and every preview stuck permanently after its first
  // commit. Nothing in this app runs StrictMode today (grepped), so this was inert — and would
  // have become a live bug the day someone turned it on, with nothing pointing back here.
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;
    };
  }, []);

  // N3. Bumped by every accepted write, so a settle callback that took a version earlier can tell
  // "still mine" from "someone else has written since". A ref, not state: `useBarDrag` takes a
  // version inside a pointer-event handler and reads it back inside a promise callback, neither of
  // which is a render — a state value would be a render-old snapshot in both places.
  const versionRef = useRef(0);

  const open = useCallback((baseSpan: Span, span: Span) => {
    if (!mountedRef.current) return;

    versionRef.current += 1;
    setPreview({ baseSpan, span });
  }, []);

  const clear = useCallback(() => {
    if (!mountedRef.current) return;

    versionRef.current += 1;
    setPreview(null);
  }, []);

  const version = useCallback(() => versionRef.current, []);
  const isCurrent = useCallback((taken: number) => versionRef.current === taken, []);

  return { span: preview?.span, baseSpan: preview?.baseSpan, open, clear, version, isCurrent };
};
